import { useNavigate } from 'react-router-dom'
import { Boxes, CalendarClock, Loader2, PackageCheck, Plus } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PERMISSIONS as P, usePermission } from '@/lib/permissions'
import { useGeneratePickList, usePreviewPickList, useRecordPickListPrint } from '@/hooks/usePickLists'
import { printPickSheet } from './printPickSheet'
import type { GeneratePickListPayload } from '@/types/pickList'

interface Props {
  /** What the sheet should cover. One prop, three call sites (kit build / form / step). */
  scope: GeneratePickListPayload
  /**
   * Controlled open state, owned by the parent list.
   *
   * Deliberately NOT local state: with one menu per row, independent state let
   * several menus sit open at once and none of them closed when another was
   * opened. A single "which row is open" value in the parent makes that
   * impossible to express.
   */
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Which edge the panel hangs from. Defaults to `start` because the trigger
   * sits immediately after a label (the MRN, a form name), so the panel should
   * open under that label rather than off to the row's right.
   */
  align?: 'start' | 'end'
}

export function PickListActionsMenu({ scope, open, onOpenChange, align = 'start' }: Props) {
  const navigate = useNavigate()
  const { can } = usePermission()
  const preview = usePreviewPickList()
  const generate = useGeneratePickList()
  const recordPrint = useRecordPickListPrint()

  const canView = can(P.inventoryView)
  const canGenerate = can(P.pickListsGenerate)
  if (!canView && !canGenerate) return null

  const busy = preview.isPending || generate.isPending
  const error = preview.error ?? generate.error

  const handlePrint = () => {
    preview.mutate(scope, {
      onSuccess: pickList => {
        onOpenChange(false)
        // The sheet already being picked, when there is one, picks and all
        // (z8nrz7fn5r); its trail records the print, as the pick list page does.
        printPickSheet(pickList)
        if (pickList.id && canView) recordPrint.mutate(pickList.id)
      },
    })
  }

  const handleReview = () => {
    generate.mutate(scope, {
      onSuccess: pickList => {
        onOpenChange(false)
        navigate(`/inventory/pick-lists/${pickList.id}`)
      },
    })
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label='Inventory actions'
          title='Inventory actions'
          className='flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground'
          // The row is a link with BOTH onClick and onKeyDown (Enter/Space), and
          // child events bubble — without stopping both, using this menu also
          // navigates away.
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          {/* A PLUS, not an overflow "…", per the client's mock: this menu adds
              something (a pick list, a review), it is not a grab-bag of row
              overflow actions — and it sits beside a label rather than at the
              row's trailing edge. */}
          {busy ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Plus className='h-4 w-4' strokeWidth={2.5} />
          )}
        </button>
      </PopoverTrigger>

      {/* Laid out to the client's mock: an ACTIONS caption over a rule, then
          three roomy rows. Wider and airier than the app's usual dense popover
          because this one is used on a cleanroom monitor at arm's length. */}
      <PopoverContent
        align={align}
        className='w-64 p-0'
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <p className='px-4 pb-2.5 pt-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground'>
          Actions
        </p>
        <div className='border-t' />

        <div className='p-1.5'>
          {canView && (
            <MenuItem
              icon={<PackageCheck className='h-5 w-5' />}
              onClick={handlePrint}
              disabled={busy}
            >
              Print Pick List
            </MenuItem>
          )}
          {canGenerate && (
            <MenuItem icon={<Boxes className='h-5 w-5' />} onClick={handleReview} disabled={busy}>
              Review Inventory
            </MenuItem>
          )}
          {/* Equipment List is specified alongside these two but has no backend
              yet; shown disabled so the menu matches the spec without pretending
              to work. Deliberately NOT enabled to match the mock's styling — a
              dead control that looks live is worse than a visibly dim one. */}
          <MenuItem icon={<CalendarClock className='h-5 w-5' />} onClick={() => {}} disabled>
            Equipment List
          </MenuItem>
        </div>

        {error && (
          <p className='px-4 pb-3 text-xs text-destructive'>
            {error instanceof Error ? error.message : 'Could not build the pick list.'}
          </p>
        )}
      </PopoverContent>
    </Popover>
  )
}

function MenuItem({
  icon,
  onClick,
  disabled,
  children,
}: {
  icon: React.ReactNode
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className='flex w-full items-center gap-3 rounded-md px-2.5 py-2.5 text-left text-[15px] hover:bg-muted disabled:pointer-events-none disabled:opacity-40'
    >
      <span className='shrink-0 text-muted-foreground'>{icon}</span>
      {children}
    </button>
  )
}
